import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * Demo Page V2 - Mode démo sans compte
 * Wizard simplifié en 3 étapes pour analyser un immeuble
 * À la fin: demande inscription pour voir les résultats
 */
const DemoPageV2 = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyUrl: '',
    purchasePrice: '',
    propertyType: 'multiplex',
    units: '',
    monthlyRent: '',
    downPaymentPercent: '20',
    interestRate: '5.5',
    amortization: '25'
  });

  const totalSteps = 3;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      setStep(4);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.purchasePrice && formData.units;
      case 2:
        return formData.monthlyRent;
      case 3:
        return formData.downPaymentPercent && formData.interestRate;
      default:
        return true;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '1rem 1.25rem',
    fontSize: '1rem',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.05)',
    color: 'white',
    outline: 'none',
    transition: 'all 0.2s'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: 'rgba(255,255,255,0.9)'
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Icon name="Building2" size={32} color="#60a5fa" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Décris l'immeuble
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                Quelques infos de base pour commencer
              </p>
            </div>

            <div>
              <label style={labelStyle}>
                <Icon name="Link" size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                URL de l'annonce (optionnel)
              </label>
              <input
                type="url"
                placeholder="https://www.centris.ca/..."
                value={formData.propertyUrl}
                onChange={(e) => handleChange('propertyUrl', e.target.value)}
                style={inputStyle}
              />
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem' }}>
                L'IA peut remplir automatiquement les infos
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Prix demandé *</label>
                <input
                  type="number"
                  placeholder="500 000"
                  value={formData.purchasePrice}
                  onChange={(e) => handleChange('purchasePrice', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Nombre de logements *</label>
                <input
                  type="number"
                  placeholder="4"
                  value={formData.units}
                  onChange={(e) => handleChange('units', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Type de propriété</label>
              <select
                value={formData.propertyType}
                onChange={(e) => handleChange('propertyType', e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="duplex">Duplex</option>
                <option value="triplex">Triplex</option>
                <option value="multiplex">Quadruplex</option>
                <option value="5plex">5-plex</option>
                <option value="6plex+">6-plex et plus</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.2))',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Icon name="DollarSign" size={32} color="#22c55e" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Revenus mensuels
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                Combien rapporte l'immeuble?
              </p>
            </div>

            <div>
              <label style={labelStyle}>Revenus locatifs totaux (mensuel) *</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255,255,255,0.5)'
                }}>$</span>
                <input
                  type="number"
                  placeholder="3 200"
                  value={formData.monthlyRent}
                  onChange={(e) => handleChange('monthlyRent', e.target.value)}
                  style={{ ...inputStyle, paddingLeft: '2rem' }}
                />
              </div>
            </div>

            {formData.units && (
              <div style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <p style={{ fontSize: '0.875rem', color: '#60a5fa' }}>
                  <Icon name="Lightbulb" size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Estimation: {formData.units} logements × ~800$/mois = ~{parseInt(formData.units) * 800}$/mois
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(168, 85, 247, 0.2))',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Icon name="Percent" size={32} color="#a855f7" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Financement
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                Comment comptes-tu financer?
              </p>
            </div>

            <div>
              <label style={labelStyle}>Mise de fonds (%)</label>
              <input
                type="number"
                placeholder="20"
                value={formData.downPaymentPercent}
                onChange={(e) => handleChange('downPaymentPercent', e.target.value)}
                style={inputStyle}
              />
              {formData.purchasePrice && formData.downPaymentPercent && (
                <p style={{ fontSize: '0.875rem', color: '#22c55e', marginTop: '0.5rem' }}>
                  = {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(parseFloat(formData.purchasePrice) * parseFloat(formData.downPaymentPercent) / 100)}
                </p>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Taux d'intérêt (%)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="5.5"
                  value={formData.interestRate}
                  onChange={(e) => handleChange('interestRate', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Amortissement (ans)</label>
                <select
                  value={formData.amortization}
                  onChange={(e) => handleChange('amortization', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="20">20 ans</option>
                  <option value="25">25 ans</option>
                  <option value="30">30 ans</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
            }}>
              <Icon name="CheckCircle" size={40} color="white" />
            </div>
            
            <h2 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1rem' }}>
              Analyse prête!
            </h2>
            
            <p style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '1.125rem',
              marginBottom: '2rem',
              lineHeight: 1.6
            }}>
              Ton analyse est calculée.<br />
              Crée un compte gratuit pour voir les résultats.
            </p>

            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '16px',
              padding: '1.5rem',
              marginBottom: '2rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{ filter: 'blur(8px)', opacity: 0.5 }}>
                <div style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Cashflow mensuel</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#22c55e' }}>+$XXX/mois</div>
              </div>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0,0,0,0.8)',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: '0.875rem'
              }}>
                <Icon name="Lock" size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Inscription requise
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button
                onClick={() => {
                  localStorage.setItem('pendingAnalysis', JSON.stringify(formData));
                  navigate('/register');
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>Créer mon compte gratuit</span>
                <Icon name="ArrowRight" size={18} />
              </button>
              
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '0.875rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                J'ai déjà un compte
              </button>
            </div>

            <p style={{ 
              fontSize: '0.75rem', 
              color: 'rgba(255,255,255,0.4)',
              marginTop: '1.5rem'
            }}>
              Aucune carte de crédit requise
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <header style={{
        padding: '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => navigate('/v2')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem'
          }}
        >
          <Icon name="ArrowLeft" size={18} />
          <span>Retour</span>
        </button>

        {step < 4 && (
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.5)' }}>
            Étape {step} sur {totalSteps}
          </div>
        )}
      </header>

      {step < 4 && (
        <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
          <div style={{
            height: '4px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(step / totalSteps) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '480px',
        margin: '0 auto',
        padding: '0 2rem 2rem',
        width: '100%'
      }}>
        <div style={{ flex: 1 }}>
          {renderStep()}
        </div>

        {step < 4 && (
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            {step > 1 && (
              <button
                onClick={prevStep}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Précédent
              </button>
            )}
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              style={{
                flex: step === 1 ? 1 : 2,
                padding: '1rem',
                background: canProceed() 
                  ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
                  : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '12px',
                color: canProceed() ? 'white' : 'rgba(255,255,255,0.3)',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{step === totalSteps ? 'Voir les résultats' : 'Continuer'}</span>
              <Icon name="ArrowRight" size={18} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default DemoPageV2;
